import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { PIKKO_APP_DEEPLINK } from '../../../utils/appStoreRedirect';
import { formatJackpotAmount, getJackpotTargetAmount, animateJackpotCounter } from '../../../utils/helper-functions';
import pikkocoin from '../../../styles/img/1sur3.svg';
import pikkoLogo from '../../../styles/img/logostatic.svg';

const CARD_DURATION = 600;

const AppTopSection = ({ count, amountVisible, cardVisible }) => (
  <div className="relative bg-gradient-to-br from-[#FEFCE8] via-[#FEF9C3] to-[#FEF08A] px-6 pt-7 pb-8 overflow-hidden">
    {/* Subtle ambient glows for yellow theme */}
    <div className="absolute -top-12 -left-10 w-48 h-48 bg-[#FDE047]/40 rounded-full blur-[40px] pointer-events-none mix-blend-multiply" />
    <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-[#EAB308]/30 rounded-full blur-[35px] pointer-events-none mix-blend-multiply" />

    {/* Floating Coins */}
    <img
      src={pikkocoin}
      alt="coin"
      className={clsx(
        "absolute top-24 opacity-50 right-20 w-8 h-8 pointer-events-none transition-all duration-[1200ms] ease-out",
        {
          "translate-y-0 scale-100 rotate-12": cardVisible,
          "translate-y-8 scale-50 -rotate-45": !cardVisible,
        }
      )}
      style={{ transitionDelay: '300ms', ...(cardVisible ? { animation: 'floatReminder 4s ease-in-out infinite 0.5s' } : {}) }}
    />
    <img
      src={pikkocoin}
      alt="coin"
      className={clsx(
        "absolute -bottom-2 left-[45%] w-6 h-6 pointer-events-none transition-all duration-[1200ms] ease-out",
        {
          "translate-y-0 scale-100 -rotate-12": cardVisible,
          "translate-y-8 scale-50 rotate-45": !cardVisible,
        }
      )}
      style={{ transitionDelay: '450ms', ...(cardVisible ? { animation: 'floatReminder 3.5s ease-in-out infinite 1.2s' } : {}) }}
    />
    <img
      src={pikkocoin}
      alt="coin"
      className={clsx(
        "absolute top-10 right-2 w-10 h-10 opacity-50 pointer-events-none transition-all duration-[1200ms] ease-out",
        {
          "translate-y-0 scale-100 rotate-45": cardVisible,
          "translate-y-8 scale-50 rotate-0": !cardVisible,
        }
      )}
      style={{ transitionDelay: '600ms', ...(cardVisible ? { animation: 'floatReminder 5s ease-in-out infinite 0.2s' } : {}) }}
    />

    {/* Shine Sweep Effect for the top card */}
    <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
      <div
        className={clsx(
          "absolute top-0 bottom-0 w-[200%] bg-gradient-to-r from-transparent via-white/80 to-transparent -skew-x-[25deg] transition-transform duration-[1400ms] ease-in-out",
          {
            "translate-x-[150%]": cardVisible,
            "-translate-x-[150%]": !cardVisible,
          }
        )}
        style={{ transitionDelay: '250ms' }}
      />
    </div>

    <div className="flex flex-row justify-between items-center relative z-10 w-full">
      {/* Text Content */}
      <div className="flex flex-col flex-1 pr-2">
        <span
          className={clsx(
            "text-[11px] uppercase tracking-wider text-[#A16207] mb-1.5 transition-all duration-700 ease-out",
            {
              "opacity-100 translate-x-0": cardVisible,
              "opacity-0 -translate-x-4": !cardVisible,
            }
          )}
          style={{ fontFamily: 'geomanistbold', transitionDelay: '100ms' }}
        >
          Cagnotte PikkoPay
        </span>

        <div
          className={clsx(
            "flex items-end gap-1 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            {
              "opacity-100 translate-x-0": amountVisible,
              "opacity-0 -translate-x-4": !amountVisible,
            }
          )}
          style={{ transitionDelay: '200ms' }}
        >
          <div className="relative geomanistBold text-[42px] leading-[1] tracking-tight inline-block" style={{ letterSpacing: "-0.5px" }}>
            <span
              className="absolute inset-0"
              style={{
                WebkitTextStroke: "5px #EAB308",
                color: "transparent"
              }}
            >
              {formatJackpotAmount(count, true, '0,00')}€
            </span>
            <span
              style={{
                background: "linear-gradient(135deg, #FDE047 0%, #CA8A04 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                position: "relative",
                filter: "drop-shadow(0px 3px 0px #854D0E)"
              }}
            >
              {formatJackpotAmount(count, true, '0,00')}€
            </span>
          </div>
        </div>

        <p
          className={clsx(
            "text-[15px] leading-tight text-gray-800 mt-1.5 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            {
              "opacity-100 translate-x-0": cardVisible,
              "opacity-0 -translate-x-4": !cardVisible,
            }
          )}
          style={{ fontFamily: 'geomanistmedium', transitionDelay: '300ms' }}
        >
          sur l'application mobile
        </p>
      </div>

      {/* Mascot Element */}
      <div
        className={clsx(
          "relative flex-shrink-0 flex items-center justify-center transition-all duration-[900ms] ease-[cubic-bezier(0.34,1.56,0.64,1)]",
          {
            "opacity-100 scale-100 rotate-0 translate-x-0": cardVisible,
            "opacity-0 scale-50 rotate-[-20deg] translate-x-10": !cardVisible,
          }
        )}
        style={{ transitionDelay: '200ms' }}
      >
        <img
          src={pikkoLogo}
          alt="mascot"
          className="w-20 h-20 object-contain drop-shadow-[0_8px_16px_rgba(234,179,8,0.30)]"
          style={cardVisible ? { animation: 'floatReminder 3s ease-in-out infinite 1s' } : {}}
        />
      </div>
    </div>
  </div>
);

const AppDivider = ({ cardVisible }) => (
  <div className="relative w-full h-0 flex items-center justify-center z-20">
    <div className="absolute -left-3 w-6 h-6 bg-[#F8F9FA] rounded-full shadow-[inset_-3px_0_6px_rgba(0,0,0,0.06)] border border-[#EDEDED] pointer-events-none" />
    <div className="w-full border-t-[2.5px] border-dashed border-[#E3E3E3] mx-5" />
    <div className="absolute -right-3 w-6 h-6 bg-[#F8F9FA] rounded-full shadow-[inset_3px_0_6px_rgba(0,0,0,0.06)] border border-[#EDEDED] pointer-events-none" />

    <div
      className={clsx(
        "absolute inline-flex items-center gap-1.5 px-4 py-1.5 bg-white rounded-full border border-[#EBEBEB] shadow-[0_4px_16px_rgba(0,0,0,0.06)] transition-all duration-[800ms] ease-[cubic-bezier(0.34,1.56,0.64,1)]",
        {
          "opacity-100 scale-100": cardVisible,
          "opacity-0 scale-[0.6]": !cardVisible,
        }
      )}
      style={{ transitionDelay: '450ms' }}
    >
      <span className="text-[14px]">🛒</span>
      <span
        className="text-[12px] text-gray-700 tracking-tight"
        style={{ fontFamily: 'geomanistmedium' }}
      >
        Utilisables <strong className="text-gray-900" style={{ fontFamily: 'geomanistbold' }}>tout de suite</strong>
      </span>
    </div>
  </div>
);

const AppBottomSection = ({ cardVisible }) => (
  <div className="relative bg-white px-6 pt-10 pb-6">
    <div
      className={clsx(
        "w-full transition-all duration-[800ms] ease-[cubic-bezier(0.16,1,0.3,1)]",
        {
          "opacity-100 translate-y-0 scale-100": cardVisible,
          "opacity-0 translate-y-6 scale-[0.96]": !cardVisible,
        }
      )}
      style={{ transitionDelay: '550ms' }}
    >
      <button
        onClick={() => window.open(PIKKO_APP_DEEPLINK, '_blank', 'noopener')}
        className="w-full flex items-center justify-center gap-2 py-[17px] text-[16px] text-white bg-black rounded-3xl shadow-[0_12px_24px_-8px_rgba(0,0,0,0.35)] shimmer-button overflow-hidden active:scale-[0.98] transition-all duration-300"
        style={{ fontFamily: 'geomanistbold' }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="animate-pulse"
        >
          <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
          <line x1="12" y1="18" x2="12.01" y2="18" />
        </svg>
        Ouvrir l'app PikkoPay
      </button>
    </div>

    <p
      className={clsx(
        "mt-6 text-center text-[13px] text-gray-400 transition-all duration-700 ease-out",
        {
          "opacity-100 translate-y-0": cardVisible,
          "opacity-0 translate-y-3": !cardVisible,
        }
      )}
      style={{ fontFamily: 'geomanistmedium', transitionDelay: '650ms' }}
    >
      Retrouve ta cagnotte <span className="text-yellow-600" style={{ fontFamily: 'geomanistbold' }}>dans ton espace</span>
    </p>
  </div>
);


export const PikkoJackpotHasAppCard = ({ offer }) => {
  const cardRef = useRef(null);
  const timersRef = useRef([]);
  const [isInView, setIsInView] = useState(false);
  const [cardVisible, setCardVisible] = useState(false);
  const [amountVisible, setAmountVisible] = useState(false);
  const [count, setCount] = useState(1);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;

    const startAnimation = () => {
      setIsInView(true);
      const t1 = setTimeout(() => setCardVisible(true), 16);
      const t2 = setTimeout(() => {
        setAmountVisible(true);
        const targetAmount = getJackpotTargetAmount(offer?.balance);
        animateJackpotCounter(targetAmount, setCount, timersRef);
      }, CARD_DURATION * 0.9);

      timersRef.current = [t1, t2];
    };

    const intersectionObs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        intersectionObs.disconnect();

        const modalEl = document.getElementById('jackpot-offert-modale');
        const isModalOpen = modalEl && !modalEl.classList.contains('opacity-0');

        if (isModalOpen) {
          window.addEventListener('pikkoJackpotModalClosed', startAnimation, { once: true });
        } else {
          startAnimation();
        }
      },
      { threshold: 0.2 },
    );

    intersectionObs.observe(el);

    return () => {
      intersectionObs.disconnect();
      window.removeEventListener('pikkoJackpotModalClosed', startAnimation);
      timersRef.current.forEach(clearTimeout);
    };
  }, []);

  return (
    <div
      ref={cardRef}
      className="flex flex-col gap-4 w-full text-black"
    >
      <div
        className={clsx(
          "flex flex-col relative w-full bg-white rounded-3xl shadow-[0_12px_40px_-12px_rgba(0,0,0,0.12)] border border-[#EDEDED] transition-all duration-[1000ms] ease-[cubic-bezier(0.16,1,0.3,1)] overflow-hidden",
          {
            "opacity-100 translate-y-0 scale-100": cardVisible,
            "opacity-0 translate-y-12 scale-[0.96]": !cardVisible,
          }
        )}
      >
        <AppTopSection count={count} amountVisible={amountVisible} cardVisible={cardVisible} />
        <AppDivider cardVisible={cardVisible} />
        <AppBottomSection cardVisible={cardVisible} />
      </div>
    </div>
  );
};
